import React, { useCallback, useState } from 'react'
import { ControllerRenderProps } from 'react-hook-form'
import { gql, useMutation } from '@apollo/client'
import { Button } from '@/components/ui/button'
import { VideoIcon, Cross2Icon, ReloadIcon } from '@radix-ui/react-icons'

const UPLOAD_VIDEO = gql`
  mutation uploadVideo($file: Upload!) {
    uploadVideo(file: $file)
  }
`

// Soft (non-blocking) size guidance only — large self-hosted hero videos hurt
// page load, but we still let the upload through if the user insists.
const RECOMMENDED_MAX_BYTES = 15 * 1024 * 1024

interface VideoUploadV2Props {
  field: ControllerRenderProps<any, any>
}

export function VideoUploadV2({ field }: VideoUploadV2Props) {
  const [uploadVideo] = useMutation(UPLOAD_VIDEO)
  const [isUploading, setIsUploading] = useState(false)
  const [videoPreview, setVideoPreview] = useState<string | null>(null)
  const [sizeWarning, setSizeWarning] = useState<string | null>(null)

  const uploadFile = useCallback(
    async (file: File) => {
      setSizeWarning(
        file.size > RECOMMENDED_MAX_BYTES
          ? `This video is ${(file.size / (1024 * 1024)).toFixed(
              1
            )} MB. For faster page loads, consider uploading a smaller, web-optimized MP4 (under ~15 MB).`
          : null
      )

      setIsUploading(true)
      try {
        // Create preview URL
        const previewUrl = URL.createObjectURL(file)
        setVideoPreview(previewUrl)

        const { data } = await uploadVideo({
          variables: { file },
        })

        if (data?.uploadVideo) {
          // The mutation returns the public video URL directly
          field.onChange(data.uploadVideo)
        }
      } catch (error) {
        console.error('Upload error:', error)
        setVideoPreview(null)
      } finally {
        setIsUploading(false)
      }
    },
    [field.onChange, uploadVideo]
  )

  const handleVideoUpload = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0]
      if (!file) return
      await uploadFile(file)
    },
    [uploadFile]
  )

  const handleRemoveVideo = () => {
    field.onChange(null)
    setSizeWarning(null)
    // Clean up the preview URL
    if (videoPreview) {
      URL.revokeObjectURL(videoPreview)
    }
    setVideoPreview(null)
  }

  const [isDragging, setIsDragging] = useState(false)

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
  }, [])

  const handleDragEnter = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(true)
  }, [])

  const handleDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)
  }, [])

  const handleDrop = useCallback(
    async (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault()
      e.stopPropagation()
      setIsDragging(false)

      const file = e.dataTransfer.files?.[0]
      if (!file || !file.type.startsWith('video/')) return
      await uploadFile(file)
    },
    [uploadFile]
  )

  const hasVideo = field.value || videoPreview

  return (
    <div className="col-span-2 w-full">
      <div
        className={`w-full ${
          !hasVideo
            ? 'p-4 border-2 border-dashed rounded-lg transition-colors ' +
              (isDragging ? 'border-primary bg-primary/5' : 'border-gray-200')
            : ''
        }`}
        onDragOver={handleDragOver}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        {/* Video Preview */}
        {hasVideo && (
          <div className="relative group border rounded-md overflow-hidden inline-block">
            <video
              src={videoPreview ?? field.value}
              muted
              controls
              className="w-32 h-20 object-cover bg-black"
            />
            <div className="absolute top-1 right-1">
              <Button
                variant="ghost"
                size="sm"
                className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100 bg-white/80 hover:bg-white/90"
                onClick={handleRemoveVideo}
              >
                <Cross2Icon className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}

        {/* Upload Button - Only shown if no video */}
        {!hasVideo && (
          <div className="flex items-center justify-between gap-4 w-full">
            <p className="text-sm text-gray-500">Drag and drop a video here</p>
            <input
              type="file"
              id={`video-upload-${field.name}`}
              accept="video/mp4,video/webm"
              className="hidden"
              onChange={handleVideoUpload}
            />
            <Button
              type="button"
              variant="outline"
              onClick={() =>
                document.getElementById(`video-upload-${field.name}`)?.click()
              }
              disabled={isUploading}
            >
              {isUploading ? (
                <ReloadIcon className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <VideoIcon className="mr-2 h-4 w-4" />
              )}
              {isUploading ? 'Uploading...' : 'Upload Video'}
            </Button>
          </div>
        )}
      </div>

      {/* Soft size guidance — non-blocking, upload already went through above. */}
      {sizeWarning && (
        <p className="mt-2 text-xs text-amber-600">{sizeWarning}</p>
      )}
    </div>
  )
}
